import type { JSX, ReactNode } from 'react';
import type { ComposerFile } from './use-attachments';

const TONES = {
  plain: {
    className: 'border border-line bg-transparent text-mute hover:text-ink hover:border-acc',
    style: undefined,
  },
  bad: {
    className: 'border',
    style: {
      borderColor: 'var(--bad)',
      background: 'color-mix(in oklch, var(--bad) 15%, transparent)',
      color: 'var(--bad)',
    },
  },
  send: { className: 'border-0 bg-acc text-acc-ink', style: undefined },
  off: { className: 'border-0 bg-surf2 text-mute', style: undefined },
} as const;

/** One of the composer's 34px square buttons. */
export function ComposerButton({
  title,
  onClick,
  tone = 'plain',
  disabled = false,
  children,
}: {
  title: string;
  onClick: () => void;
  tone?: keyof typeof TONES;
  disabled?: boolean;
  children: ReactNode;
}): JSX.Element {
  const look = TONES[tone];
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`relative grid size-[34px] place-items-center rounded-[9px] transition-all duration-200 ${look.className}`}
      style={look.style}
    >
      {children}
    </button>
  );
}

/** The files picked for the next question; a chip still uploading is dimmed. */
export function AttachmentChips({
  files,
  onRemove,
}: {
  files: readonly ComposerFile[];
  onRemove: (key: string) => void;
}): JSX.Element | null {
  if (files.length === 0) return null;
  return (
    <div className="mb-2 flex flex-wrap gap-1.5">
      {files.map((f) => (
        <span
          key={f.key}
          className="border-line bg-surf2 inline-flex items-center gap-2 rounded-lg border px-2.5 py-[5px] font-mono text-xs"
          style={{ opacity: f.ref === undefined ? 0.6 : 1 }}
          title={f.ref === undefined ? 'Uploading…' : f.name}
        >
          {f.name}
          <button
            type="button"
            onClick={() => onRemove(f.key)}
            aria-label={`Remove ${f.name}`}
            className="text-mute hover:text-ink border-0 bg-transparent p-0 text-[13px] leading-none"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

const icon = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function PaperclipIcon(): JSX.Element {
  return (
    <svg {...icon} strokeWidth={2}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

export function MicIcon(): JSX.Element {
  return (
    <svg {...icon} strokeWidth={2}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0M12 17v5" />
    </svg>
  );
}

export function SendIcon(): JSX.Element {
  return (
    <svg {...icon} strokeWidth={2.2}>
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}

/** The line under the box: what is happening now, on the left. */
export function composerHint(voice: string, busy: boolean): string {
  if (voice === 'listening') return 'Listening… tap the mic again to stop';
  if (voice === 'transcribing') return 'Transcribing…';
  return busy ? 'Agent is answering' : '';
}
